import './index.scss';
import React, { use, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client/react';
import { COMPONENT_NAME as REVIEW_CONTENT_COMPONENT_NAME } from './review-content';
import { BasicDropDown } from '@components-clean/core/basic-dropdown';
import Card from '@components-clean/core/card';
import ChipList from '@components-clean/core/chip-list';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import { formatDatasetDates, sortDatasetByDatesDesc } from '@components-clean/navbar/dataset-selector.utils';
import { usePageToolbar } from '@contexts-clean/page-toolbar-context';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { useUnsavedChangesConfirmation } from '@hooks-clean/index';
import { PanelDto, CM2Dataset } from '@interfaces/central-monitoring';
import PanelChart from '@pages-clean/central-monitoring/components/chart-panel';
import { MonitoringToolkitContext } from '@pages-clean/central-monitoring/monitoring-toolkit/context';
import { ReviewRAGChangeChipValues } from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/kri-overview/constants';
import KriHistoryGrid from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/kri-overview/kri-grid';
import { ReviewRagSection } from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/kri-overview/review-rag-card';
import { useBoolean } from '@hooks/index';
import { GET_STUDY_HUB_MONITORING_TOOLKIT_CONFIGURATION } from '@queries/central-monitoring';
import {
    getMonitoringToolkitConfig,
    getMonitoringToolkitConfigVariables,
} from '@queries/generated/central-monitoring.generated';
import DatasetApi from '@apis/bff-api/central-monitoring/dataset';
import KriOverviewApi from '@apis/bff-api/central-monitoring/kri-overview';

interface IProps {
    studyRef: string;
    canEdit: boolean;
    datasetId?: number;
}

const COMPONENT_NAMES = [REVIEW_CONTENT_COMPONENT_NAME];

const KRIOverview = ({ studyRef, canEdit, datasetId }: IProps) => {
    const { t }: { t: (key: string) => string } = useTranslation('cm', {
        keyPrefix: 'monitoringToolkit.kriOverview',
    });
    const [selectedDataset, setSelectedDataset] = useState<CM2Dataset | undefined>();
    const [prevSelectedPanel, setPrevSelectedPanel] = useState<PanelDto | undefined>(undefined);
    const [selectedPanel, setSelectedPanel] = useState<PanelDto | undefined>(undefined);
    const [selectedComparisonDataset, setSelectedComparisonDataset] = useState<CM2Dataset | undefined>();
    const [dataviewRef, setDataviewRef] = useState<string | undefined | null>(undefined);
    const { bottomPanelRef } = use(MonitoringToolkitContext);
    const [chip, setChip] = useState<ReviewRAGChangeChipValues>(ReviewRAGChangeChipValues.RAGChanges);
    const { legendToggleValue } = usePageToolbar();
    const { data: datasets, isLoading: isLoadingDatasets } = DatasetApi.useGetActive(studyRef);
    const { centralMonitoringToolkitHasChanges } = useUnsavedChangesContext();

    const sortedDatasets = (datasets ?? []).map(formatDatasetDates).sort(sortDatasetByDatesDesc);

    const previousDatasets = useMemo(() => {
        if (!selectedDataset) return [];
        return [...sortedDatasets.filter((dataset) => new Date(dataset.created!) < new Date(selectedDataset.created!))];
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [sortedDatasets]);

    const { data: panels, isLoading: isLoadingPanels } = KriOverviewApi.useGetPanels(studyRef, selectedDataset?.id);
    const allowKRIReviewsState = useBoolean(false);

    useEffect(() => {
        if (!datasets) return;

        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setSelectedDataset(
            sortedDatasets.length > 0
                ? (sortedDatasets.find((d) => d.id === datasetId) ?? sortedDatasets[0])
                : undefined,
        );
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [datasets]);

    useEffect(() => {
        if (prevSelectedPanel && panels?.find((item) => item.reference === prevSelectedPanel?.reference))
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setSelectedPanel(prevSelectedPanel);
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [panels]);

    // If the updated datasets list no longer contains the currently selected dataset, clear down selection
    useEffect(() => {
        if (!previousDatasets.some((dataset) => dataset.id === selectedComparisonDataset?.id)) {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setSelectedComparisonDataset(undefined);
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [previousDatasets]);

    const { data: panel, isLoading: isLoadingPanel } = KriOverviewApi.useGetPanelData(
        studyRef,
        selectedDataset?.id,
        selectedPanel?.reference,
    );

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        if (panel) setDataviewRef(panel.dataviewRef);
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [panel?.reference]);

    const handleDatasetSelectorChange = (value: CM2Dataset) => {
        setPrevSelectedPanel(selectedPanel);
        setSelectedPanel(undefined);
        setSelectedDataset(value);
    };

    const [loadConfig] = useLazyQuery<getMonitoringToolkitConfig, getMonitoringToolkitConfigVariables>(
        GET_STUDY_HUB_MONITORING_TOOLKIT_CONFIGURATION,
    );

    useEffect(() => {
        loadConfig({ variables: { studyId: studyRef } })
            .then((response) =>
                allowKRIReviewsState.setValue(response.data?.getMonitoringToolkitConfig?.allowKRIReviews ?? false),
            )
            .catch((err) => window.console.error(err));
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [studyRef]);

    const anyChanges = centralMonitoringToolkitHasChanges(REVIEW_CONTENT_COMPONENT_NAME);

    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'central-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAMES,
    });

    return (
        <div className="kri-overview">
            <Card
                data-testid="topbar-dropdowns"
                bodyClassName="topbar-content"
                className="topbar-styling topbar-splitters"
            >
                <div className="selector-area">
                    <BasicDropDown
                        label={`${t('selectors.dataset.label')}`}
                        monitoringToolkitTopbar
                        data-testid="dataset-selector"
                        data={sortedDatasets}
                        dataItemKey="id"
                        textField="dataCutDate"
                        value={sortedDatasets.find((d) => d.id === selectedDataset?.id)}
                        onChange={async (e) => {
                            const confirmed = await confirmLossOfChanges();
                            if (!confirmed) {
                                return;
                            }

                            const dataset = e.value as CM2Dataset;
                            handleDatasetSelectorChange(dataset);
                        }}
                        loading={isLoadingDatasets}
                        filterable
                        className="dropdown-dataset-picker"
                        customClassName="basic-dropdown-styling"
                        closeWhenThisIsScrolled={bottomPanelRef.current}
                        allowClear={false}
                        cardHeader
                    />
                    <BasicDropDown
                        label={`${t('selectors.panel.label')}`}
                        monitoringToolkitTopbar
                        data-testid="panel-selector"
                        data={panels ?? []}
                        dataItemKey="reference"
                        textField="plotTitle"
                        onChange={async (e) => {
                            const confirmed = await confirmLossOfChanges();
                            if (!confirmed) {
                                return;
                            }

                            const curPanel = e.value as PanelDto;
                            setSelectedPanel(curPanel);
                        }}
                        value={panels?.find((item) => item.reference === selectedPanel?.reference) ?? null}
                        loading={isLoadingPanels}
                        filterable
                        className="dropdown-panel-picker"
                        customClassName="basic-dropdown-styling"
                        closeWhenThisIsScrolled={bottomPanelRef.current}
                        tooltipText={selectedPanel?.plotTitle}
                        allowClear={false}
                        cardHeader
                    />
                    <BasicDropDown
                        label={`${t('selectors.comparisonDataset.label')}`}
                        monitoringToolkitTopbar
                        data-testid="comparison-dataset-selector"
                        data={previousDatasets}
                        dataItemKey="id"
                        textField="dataCutDate"
                        value={previousDatasets.find((d) => d.id === selectedComparisonDataset?.id) ?? null}
                        onChange={(e) => {
                            const dataset = e.value as CM2Dataset;
                            setSelectedComparisonDataset(dataset);
                        }}
                        filterable
                        className="dropdown-dataset-picker"
                        customClassName="basic-dropdown-styling"
                        closeWhenThisIsScrolled={bottomPanelRef.current}
                        allowClear={false}
                        cardHeader
                    />
                    <section style={{ marginLeft: 'auto', display: 'flex', gridColumn: '2' }}>
                        <ChipList
                            key={`${anyChanges}`}
                            data-testid="rag-review-chip-list"
                            defaultValue={ReviewRAGChangeChipValues.RAGChanges}
                            data={[
                                {
                                    text: t('cards.review.chipList.ragChanges'),
                                    value: ReviewRAGChangeChipValues.RAGChanges,
                                },

                                ...(allowKRIReviewsState.value
                                    ? [
                                          {
                                              text: t('cards.review.chipList.review'),
                                              value: ReviewRAGChangeChipValues.Review,
                                          },
                                      ]
                                    : []),
                            ]}
                            value={chip}
                            onChange={async (event) => {
                                const confirmed = await confirmLossOfChanges();
                                if (!confirmed) {
                                    return;
                                }

                                // The check prevents a user being able to deselect chips
                                const selectedChip = event.value as ReviewRAGChangeChipValues;
                                if (!selectedChip) return;
                                setChip(selectedChip);
                            }}
                        />
                    </section>
                </div>
            </Card>
            {panel && !isLoadingPanel ? (
                <PanelChart key={panel.reference} panel={panel} showLegend={legendToggleValue?.current} />
            ) : (
                <Card data-testid="panel" title={selectedPanel?.plotTitle ?? t('cards.panel.defaultTitle')}>
                    {isLoadingPanel ? (
                        <Loading />
                    ) : (
                        <InfoField message={t('cards.panel.emptyMessage')} type="Info" layout="center" />
                    )}
                </Card>
            )}

            <ReviewRagSection
                studyRef={studyRef}
                panelReference={selectedPanel?.reference}
                datasetData={selectedDataset}
                comparisonData={selectedComparisonDataset}
                canEdit={canEdit}
                dataviewRef={dataviewRef}
                allowKRIReviewsState={allowKRIReviewsState.value}
                selectedChip={chip}
                panelTitle={selectedPanel?.plotTitle}
                includePurpleSites={panel?.includePurpleSites}
            />
            <KriHistoryGrid studyRef={studyRef} panel={selectedPanel} datasetId={selectedDataset?.id} />
        </div>
    );
};

export default KRIOverview;
